import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { MioExpression, MioImage } from 'src/components/Common/MioImage'
import { UIButton, type UIButtonProps } from 'src/components/ui/Button'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useCancelRunningTask } from 'src/services/orpc/task'

/**
 * Cancel a long-running task with refund.
 * For tasks that have been running for at least 24 hours (cancelability === CANCELABLE).
 */
export const CancelAndRefundTaskButton: FC<
  {
    taskId: string
    onSuccess?: () => void
  } & Omit<UIButtonProps, 'onPress'>
> = ({ taskId, onSuccess, children, ...props }) => {
  const { t } = useTranslation(['artwork'])
  const { mutateAsync: cancelTask } = useCancelRunningTask()

  return (
    <UIButton
      variant="filled"
      color="danger"
      {...props}
      onPress={async () => {
        const toProceed = await UIDialog.confirm({
          heading: <Text i18nKey="artwork:task.cancel.title-with-refund" />,
          content: (
            <div className="flex flex-col items-center gap-3">
              <MioImage expression={MioExpression.Cry} className="h-32" />
              <Text i18nKey="artwork:task.cancel.info-with-refund" />
            </div>
          ),
          cancelProps: {
            children: <Text i18nKey="action:no" />,
          },
          confirmProps: {
            children: <Text i18nKey="action:yes" />,
          },
        })
        if (!toProceed) return

        try {
          await cancelTask({ id: taskId })
          toast.success(t('artwork:cancelled'))
          onSuccess?.()
        } catch (e: any) {
          toast.error(e?.message)
        }
      }}
    >
      {children ?? t('artwork:free-cancel-generation')}
    </UIButton>
  )
}
